import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import { API_CONFIG } from '../config/api';

export interface Content {
  id: string;
  name: string;
  description?: string;
  type: string;
  url: string;
  createdAt: Date;
  updatedAt: Date;
  materialId?: string | null;
}

interface ContentsListState {
  contents: Content[];
  isLoading: boolean;
  error: string | null;
}

export const useContentsList = () => {
  const [state, setState] = useState<ContentsListState>({
    contents: [],
    isLoading: false,
    error: null,
  });

  const { fetchWithAuth } = useFetch();

  const fetchContents = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await fetchWithAuth(`${API_CONFIG.BASE_URL}/contents`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar conteúdos: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // Mapear os campos da API para o formato esperado
      const contents = data.contents.map((content: any) => ({
        id: content.id,
        name: content.name,
        description: content.description,
        type: content.type,
        url: content.url,
        createdAt: new Date(content.createdAt),
        updatedAt: new Date(content.updatedAt),
        materialId: content.materialId || null,
      }));

      setState(prev => ({
        ...prev,
        contents,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  // Buscar apenas conteúdos disponíveis (não linkados a materiais)
  const fetchAvailableContents = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await fetchWithAuth(`${API_CONFIG.BASE_URL}/contents/available`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar conteúdos disponíveis: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // Mapear os campos da API para o formato esperado
      const contents = (await Promise.all(data.contents)).map((content: any) => ({
        id: content.id,
        name: content.name,
        description: content.description,
        type: content.type,
        url: content.url,
        createdAt: new Date(content.createdAt),
        updatedAt: new Date(content.updatedAt),
        materialId: null, // Conteúdos disponíveis não têm materialId
      }));

      setState(prev => ({
        ...prev,
        contents,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        isLoading: false,
      }));
    }
  }, [fetchWithAuth]);

  return {
    contents: state.contents,
    isLoading: state.isLoading,
    error: state.error,
    fetchContents,
    fetchAvailableContents,
  };
};
